"use client"

import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { blobMediaUrl } from "@/lib/blob-url"
import { formatNumber } from "@/lib/format"
import { bookingUrlForCountry, PRODUCT_IDS } from "@/lib/booking"
import { withQuizAttribution } from "@/lib/quiz/booking-source"
import { isBookable, isOffered, type QuizMarket } from "@/lib/quiz/market"
import { track } from "@/lib/analytics"
import type { PriceMap } from "@/lib/pricing"
import type { BaseTierId } from "@/lib/quiz/types"

interface Props {
  baseTier: BaseTierId
  prices: PriceMap
  market: QuizMarket
}

/**
 * Secondary "Prefer to start smaller?" card under the main
 * recommendation. Same shell + scale as `RecommendationCard`:
 * inline circular thumbnail next to the title, short body, same
 * small-CHF/medium-figure price line, tonal CTA. Tonal variant
 * signals this is an alternative path, not the next step.
 */
export function BloodTestAltCard({ baseTier, prices, market }: Props) {
  const t = useTranslations("quiz.alternative.lighter")
  const tProducts = useTranslations("quiz.products.blood_only")
  const ut = useTranslations("ui")
  const { code: country, currency } = useCountry()
  const { openWaitlist } = useWaitlist()
  // Real locale — used for the booking link. Do NOT narrow it: the previous
  // `en|fr ? locale : "de"` narrowing sent a Dutch visitor a `locale=de`
  // booking link, the same bug the main recommendation card documents fixing.
  const locale = useLocale()

  // Only MRI tiers see the cross-sell down to blood-only.
  if (baseTier === "blood_only") return null

  // Market-aware, same contract the main recommendation card follows: a market
  // that can't sell the blood test at all (`not_available` / `hidden`) drops
  // the cross-sell entirely; a `coming_soon` / `waitlist` one keeps the card
  // but swaps its booking deep-link for the waitlist modal, rather than sending
  // the visitor into a booking flow for a product the market can't sell (D3).
  const bloodAvailability = market.tiers.blood_only
  if (!isOffered(bloodAvailability)) return null
  const bookable = isBookable(bloodAvailability)

  const price = Number(prices.blood)
  // A bookable market must ship a figure; if it somehow doesn't, drop the card
  // rather than render "EUR NaN". A waitlist market has no price to show.
  if (bookable && !Number.isFinite(price)) return null

  return (
    <aside
      className={
        "mx-auto max-w-[556px] rounded-[16px] border border-white bg-white/80 p-6 transition-all duration-[250ms] hover:bg-white hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)] md:p-8"
      }
    >
      <p className="type-label-lg text-neutral-500">{t("eyebrow")}</p>

      <div className="mt-3 flex items-center gap-2">
        <Image
          src={blobMediaUrl("/images/addons/advanced-blood-test-card.png")}
          alt={tProducts("name")}
          width={28}
          height={28}
          className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
        />
        <h2 className="type-headline-lg font-medium text-neutral-900">
          {tProducts("name")}
        </h2>
      </div>

      <p className="type-body-md mt-2 text-neutral-600">{t("body")}</p>

      {/* Price — same approach as the main rec: small CHF prefix
        + medium-weight figure, both baseline-aligned. A coming-soon
        market shows the label in place of a figure it can't quote. */}
      <div className="mt-5 flex items-baseline gap-3">
        {bookable ? (
          <span className="type-headline-md font-medium text-neutral-900">
            <span className="type-body-md font-medium text-neutral-500">
              {currency}
            </span>{" "}
            {formatNumber(price, country)}
          </span>
        ) : (
          <span className="type-title-md text-neutral-500">
            {ut("comingSoon")}
          </span>
        )}
      </div>

      <Button
        variant="tonal"
        className="mt-5 w-full"
        onClick={() => {
          if (bookable) {
            const url = withQuizAttribution(
              bookingUrlForCountry(country, locale, [PRODUCT_IDS.bloodTest])
            )
            track.ctaClick("quiz_result_blood_alt_cta", "quiz-result", url)
            window.open(url, "_blank", "noopener,noreferrer")
          } else {
            track.ctaClick(
              "quiz_result_blood_alt_waitlist_cta",
              "quiz-result",
              "waitlist-modal"
            )
            openWaitlist()
          }
        }}
      >
        {bookable ? t("cta") : ut("joinWaitlist")}
      </Button>
    </aside>
  )
}
